import { useAuth } from '@agentnet/auth';
import { DataTable, EmptyState, pxToRem } from '@fluentsms/agentnet-web-components';
import { IconButton, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { CheckCircleOutline, DeleteOutlineTwoTone } from '@material-ui/icons';
import { ColDef } from 'ag-grid-enterprise';
import { searchFirmByDetails } from 'api/manageUsers/manage-users-api';
import useAsync from 'hooks/useAsync';
import { useEffect, useMemo, useRef, useState } from 'react';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import FormDrawerComponent from 'ui-kit/components/drawer/FormDrawerComponent'; // Import the FormDrawerComponent
import { firmStatus } from '../../../features/constants';

interface OptionType {
  id?: string | number;
  name: string;
  default?: boolean;
  officeName?: string;
  isTitleEscrow?: boolean;
  status?: string;
  firmIdArray?: any;
  firmStatus?: string;
}
interface AutocompleteChecklistProps {
  isAgentNetAdmin?: boolean;
  options: OptionType[];
  label: string;
  sectionTitle?: string;
  onChange: (value: any) => void;
  onDelete?: (index: any) => void;
  value?: OptionType[];
  qaBtnprefix?: string;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    table: {
      width: '100%',
      paddingBottom: theme.spacing(2),
    },
    tableBody: {
      width: '100%',
    },
    tableHeader: {
      backgroundColor: '#F2F5F6',
      borderBottom: '1px solid #D9E0E5',
      fontWeight: 600,
      fontSize: pxToRem(14),
      position: 'sticky',
      top: 0,
      zIndex: 1,
    },
    tableHeaderCell: {
      padding: pxToRem(12),
    },
    tableRow: {
      borderBottom: `1px solid #E0E0E0`,
    },
    tableCell: {
      padding: pxToRem(12),
      display: 'flex',
      alignItems: 'center',
    },
    deleteRow: {
      padding: 0,
      marginLeft: theme.spacing(1),
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    deleteCell: {
      justifyContent: 'center',
      borderLeft: `1px solid #E0E0E0`,
    },
    actionColumn: {
      flex: `0 0 ${pxToRem(48)}`,
      maxWidth: pxToRem(48),
    },
    escrowCell: {
      justifyContent: 'center',
      textAlign: 'center',
    },
    escrowColumn: {
      flex: `0 0 ${pxToRem(128)}`,
      maxWidth: pxToRem(128),
      whiteSpace: 'nowrap',
    },
    firmNameColumn: {
      flex: '1 1 0',
      maxWidth: 'none',
      minWidth: 0,
      overflow: 'hidden',
    },
    sectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(2),
      width: '100%',
      alignItems: 'center',
    },
    emptyState: {
      width: '99%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: theme.spacing(0, 0, 2),
    },
    emptyStateLabel: {
      marginBottom: theme.spacing(1),
      fontWeight: 600,
    },
    gridContainer: {
      height: 'calc(100vh - 200px) !important',
      width: '100%',
      fontFamily: theme.typography.fontFamily,
    },
    firmAccessGrid: {
      width: '100%',
      '& .ag-layout-auto-height .ag-center-cols-viewport': {
        minHeight: '0 !important',
      },
      '& .ag-layout-auto-height .ag-center-cols-container': {
        minHeight: '0 !important',
      },
    },
    actionColumnHeader: {
      borderLeft: `1px solid ${theme.palette.divider}`,
    },
    actionColumnCell: {
      borderLeft: `1px solid ${theme.palette.divider}`,
      display: 'flex',
      justifyContent: 'center',
    },
    centeredHeader: {
      '& .ag-header-cell-label': {
        justifyContent: 'center',
      },
    },
    centeredCell: {
      display: 'flex !important',
      justifyContent: 'center !important',
      alignItems: 'center !important',
      paddingLeft: '0 !important',
      paddingRight: '0 !important',
      '& .ag-cell-wrapper, & .ag-cell-value': {
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      },
    },
    rowHover: {
      '&:hover': {
        backgroundColor: 'transparent !important',
      },
    },
    container: {
      display: 'flex',
      flexDirection: 'column',
    },
  }),
);

const AutocompleteChecklist = ({
  isAgentNetAdmin = false,
  options,
  label,
  sectionTitle,
  onChange,
  onDelete,
  value,
  qaBtnprefix,
}: AutocompleteChecklistProps) => {
  const classes = useStyles();
  const [selectedOptions, setSelectedOptions] = useState<OptionType[]>(value || []);
  const [drawerOpen, setDrawerOpen] = useState(false); // State to manage drawer visibility
  const [gridApi, setGridApi] = useState<any>(null);
  const gridRef = useRef<any>(null);
  const [rowData, setRowData] = useState<any>([]);
  const [firmValue, setFirmValue] = useState<string>('');
  const [firmNameDropdown, setfirmNameDropdown] = useState<any>([{ name: '', value: '' }]);
  const firmSearchInput = {
    firmName: null,
    status: null,
    officeAddress: null,
    officeName: null,
  };
  const [firmSearchModel, setFirmSearchModel] = useState<any>(firmSearchInput);
  const { getAccessToken } = useAuth();
  const [firmIdArray, setFirmId] = useState<number[]>([]);

  const handleFirmNameClick = (
    firmName: string,
    firmId: number,
    office: string,
    isTitleEscrow: boolean,
    status: string,
    firmStatus: string,
  ) => {
    setFirmValue(firmName);
    setDrawerOpen(false);
    setFirmId((prevFirmIds) => {
      if (!prevFirmIds.includes(firmId)) {
        return [...prevFirmIds, firmId];
      }
      return prevFirmIds;
    });
    handleChange([
      {
        name: firmName,
        id: firmId,
        officeName: office,
        isTitleEscrow: isTitleEscrow,
        status: status,
        firmIdArray: firmIdArray,
        firmStatus: firmStatus,
      },
    ]);
    setfirmNameDropdown([{ name: firmName, value: firmName }]);
  };

  const firmNameCellRenderer = (params: any) => {
    return (
      <span
        style={{ color: '#0074CA', textDecoration: 'underline', cursor: 'pointer' }}
        onClick={() =>
          handleFirmNameClick(
            params.value,
            params.data.firmId,
            params.data.officeName,
            params.data.isTitleEscrow,
            params.data.status,
            params.data.firmStatus,
          )
        }
      >
        {params.value}
      </span>
    );
  };

  const firmColumnsConfig: ColDef[] = [
    {
      headerName: '',
      filter: false,
      minWidth: 60,
      maxWidth: 60,
      cellRenderer: (params: any) => {
        const isTrue = params.data.isTitleEscrow;
        if (isTrue) {
          return (
            <span>
              <CheckCircleOutline fontSize="small" style={{ color: 'green', paddingTop: 8 }} />
            </span>
          );
        } else {
          return <span></span>;
        }
      },
      tooltipValueGetter: () => {
        return 'TitleEscrow Firm';
      },
    },

    {
      headerName: 'Firm Name',
      field: 'firmName',
      sortable: true,
      filter: 'agTextColumnFilter',
      cellRenderer: firmNameCellRenderer,
      tooltipField: 'firmName',
    },
    {
      headerName: 'Office Name',
      field: 'officeName',
      sortable: true,
      filter: 'agTextColumnFilter',
      tooltipField: 'officeName',
    },
    {
      headerName: 'Status',
      field: 'status',
      sortable: true,
      filter: 'agSetColumnFilter',
      maxWidth: 200,
      tooltipField: 'status',
      filterParams: {
        values: firmStatus.map((status) => status),
      },
    },
    {
      headerName: 'Address',
      field: 'address',
      sortable: true,
      filter: 'agTextColumnFilter',
      tooltipField: 'address',
    },
  ];

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      flex: 2,
      floatingFilter: true,
      filter: true,
      sortable: true,
      resizable: true,
      editable: false,
      defaultValue: '-',
      suppressMenu: true,
      filterParams: { closeOnApply: true, suppressAndOrCondition: true },
    };
  }, []);

  const fetchFirms = async (): Promise<any[]> => {
    const token = await getAccessToken();
    const response = await searchFirmByDetails(token, firmSearchModel);
    return response;
  };

  const {
    execute: executeGetFirms,
    value: getFirmsValue,
    status: getFirmsStatus,
    errors: getFirmsError,
  } = useAsync<any[]>(fetchFirms, false);

  useEffect(() => {
    executeGetFirms().then();
  }, [firmSearchModel]);

  const actionStatus = getFirmsStatus == 'pending' ? 'pending' : 'idle';

  useEffect(() => {
    if (Array.isArray(getFirmsValue)) {
      const filterNames = new Set(value?.map((option) => option.name));
      const filteredFirms = getFirmsValue.filter((firm: any) => !filterNames.has(firm.firmName));
      setRowData(filteredFirms);
    } else {
      setRowData([]);
    }

    if (gridRef.current?.api) {
      if (rowData.length === 0) {
        gridRef.current.api.showNoRowsOverlay();
      } else {
        gridRef.current.api.hideOverlay();
      }
    }
  }, [getFirmsValue, value]);

  const handleDelete = (e: any) => {
    const newOptions = selectedOptions.filter((option) => option.id !== e.id);
    setSelectedOptions(newOptions);
    onDelete && onDelete(e);
    onChange && onChange(newOptions);
  };

  const onFilterChanged = () => {
    if (gridApi?.getDisplayedRowCount() === 0) {
      gridApi?.showNoRowsOverlay();
    } else {
      gridApi?.hideOverlay();
    }

    const filterModel = gridApi.getFilterModel() ?? {};
    const payload: any = {
      ...firmSearchInput,
    };
    if (filterModel?.firmName) {
      payload.firmName = filterModel?.firmName.filter;
    }
    if (filterModel?.address) {
      payload.officeAddress = filterModel?.address.filter;
    }
    if (filterModel?.officeName) {
      payload.officeName = filterModel?.officeName.filter;
    }
    if (filterModel?.status) {
      let statusFilter = '';
      filterModel?.status?.values?.map((item: any) => {
        if (item !== null) statusFilter = statusFilter + ',' + item;
      });
      statusFilter = statusFilter.slice(1);
      if (statusFilter === 'Active,Inactive' || statusFilter === 'Inactive,Active') payload.status = null;
      else if (statusFilter === 'Active') payload.status = true;
      else if (statusFilter === 'Inactive') payload.status = false;
      else if (statusFilter === 'Null') payload.status = null;
      else payload.status = null;
    }
    setFirmSearchModel(payload);
    gridRef.current?.api?.showLoadingOverlay();
  };

  const handleChange = (newValue: OptionType[]) => {
    setSelectedOptions((prevSelectedOptions) => {
      const existingOptionNames = new Set(prevSelectedOptions.map((option) => option.name));
      const filteredNewValues = newValue.filter((option) => !existingOptionNames.has(option.name));
      const mergedOptions = [...prevSelectedOptions, ...filteredNewValues];

      onChange && onChange(mergedOptions);
      return mergedOptions;
    });
    const payload: any = {
      ...firmSearchInput,
    };
    payload.firmName = '';
    setFirmSearchModel(payload);
  };

  const onGridReady = (params: any) => {
    if (actionStatus == 'pending') {
      gridRef.current?.api.showLoadingOverlay();
    } else {
      gridRef.current?.api.hideOverlay();
    }
    setGridApi(params.api);
  };

  const handleAddFirmClick = () => {
    setDrawerOpen(true); // Open the drawer when the button is clicked
    const selectedNames = new Set(selectedOptions.map((option) => option.name));
    const filteredFirms = rowData.filter((firm: any) => !selectedNames.has(firm.firmName));
    setRowData(filteredFirms);
  };

  const handleDismiss = () => {
    const payload: any = {
      ...firmSearchInput,
    };
    payload.firmName = '';
    setFirmSearchModel(payload);
    setDrawerOpen(false); // Close the drawer
  };

  const addFirmButton = () => (
    <AgentNetButton
      variant="contained"
      color="primary"
      size="small"
      data-testid={qaBtnprefix}
      onClick={handleAddFirmClick}
      plusIcon
    >
      Add {label}
    </AgentNetButton>
  );

  const hasSelectedFirms = (value?.length ?? 0) > 0;
  const selectedFirmColumnsConfig: ColDef[] = [
    {
      headerName: 'Title & Escrow',
      field: 'isTitleEscrow',
      minWidth: 144,
      maxWidth: 160,
      sortable: false,
      headerClass: classes.centeredHeader,
      cellClass: classes.centeredCell,
      cellRenderer: (params: any) =>
        params.value ? <CheckCircleOutline fontSize="small" style={{ color: 'green' }} /> : null,
    },
    {
      headerName: 'Firm Name',
      field: 'name',
      flex: 1,
      tooltipField: 'name',
    },
    ...(!isAgentNetAdmin
      ? [
          {
            headerName: '',
            minWidth: 56,
            maxWidth: 56,
            sortable: false,
            filter: false,
            floatingFilter: false,
            headerClass: `${classes.actionColumnHeader} left-align-header`,
            cellClass: classes.actionColumnCell,
            cellRenderer: (params: any) =>
              params.data?.id != 0 && params.node.rowIndex !== 0 ? (
                <IconButton
                  className={classes.deleteRow}
                  disableFocusRipple
                  disableRipple
                  disableTouchRipple
                  aria-label="delete firm"
                  onClick={() => handleDelete(params.data)}
                  data-testid={`${qaBtnprefix}Close${params.node.rowIndex}`}
                >
                  <DeleteOutlineTwoTone style={{ color: '#0074CA' }} />
                </IconButton>
              ) : null,
          },
        ]
      : []),
  ];
  const selectedFirmDefaultColDef: ColDef = {
    cellClass: 'left-align-column',
    headerClass: 'left-align-header',
    sortable: true,
    filter: false,
    floatingFilter: false,
    resizable: false,
    suppressMenu: true,
  };

  return (
    <>
      {(sectionTitle || (hasSelectedFirms && !isAgentNetAdmin)) && (
        <div className={classes.sectionHeader}>
          {sectionTitle && <Typography variant="h2">{sectionTitle}</Typography>}
          {hasSelectedFirms && !isAgentNetAdmin && addFirmButton()}
        </div>
      )}
      {!hasSelectedFirms && (
        <div className={classes.emptyState}>
          <EmptyState headerText="No Firms Added" descriptionText="" />
          {!isAgentNetAdmin && addFirmButton()}
        </div>
      )}
      {hasSelectedFirms && (
        <div className={`ag-theme-alpine ${classes.table} ${classes.firmAccessGrid}`}>
          <DataTable
            rowData={(value ?? []) as any[]}
            columnDefs={selectedFirmColumnsConfig}
            defaultColDef={selectedFirmDefaultColDef}
            domLayout="autoHeight"
            headerHeight={40}
            rowHeight={44}
          />
        </div>
      )}
      {drawerOpen && (
        <FormDrawerComponent
          title="Add Firm"
          open={drawerOpen}
          width={960}
          disabled={false}
          hidePrimaryAction={true}
          onDismissAction={handleDismiss}
          onSecondaryAction={handleDismiss}
        >
          <div className={`ag-theme-alpine ${classes.gridContainer}`}>
            <DataTable
              rowData={rowData}
              columnDefs={firmColumnsConfig}
              onGridReady={onGridReady}
              ref={gridRef}
              rowClass={classes.rowHover}
              defaultColDef={defaultColDef}
              gridOptions={{ suppressRowClickSelection: true }}
              onFilterChanged={onFilterChanged}
            />
          </div>
        </FormDrawerComponent>
      )}
    </>
  );
};

export default AutocompleteChecklist;
